import { createPortal } from 'react-dom';
import ModalHeader from '../../../components/common/ModalHeader';
import ActionButtons from '../../../components/common/ActionButtons';
import FormField from '../../../components/common/FormField';
import { useEffect, useMemo, useState } from 'react';
import CommentSection from '../../../components/common/CommentSection';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import {
  fetchDetailedDailySubCategories,
  selectDetailedDailyCategories,
  selectDetailedDailySubCategories,
} from '../DetailedDailySlice';
import { DetailedDailyFormData, DetailedDailyFormErrors, SaveEditDowntimeReq } from './DetailedDailyTypes';
import { showErrorToast, showSuccessToast } from '../../../components/toast/toastBridge';
import { saveDetailedDailyDowntime } from '../../../services/downtime/DetailedDailyServices';

interface AddEditDowntimeProps {
  isOpen: boolean;
  editData?: DetailedDailyFormData;
  onClose: () => void;
  onSaved?: () => void;
  wellId: number;
}

const AddEditDowntime: React.FC<AddEditDowntimeProps> = ({ isOpen, editData, onSaved, onClose, wellId }) => {
  const [formData, setFormData] = useState<DetailedDailyFormData>(editData ? { ...editData } : {});
  const [errors, setErrors] = useState<DetailedDailyFormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);
  const categories = useAppSelector(selectDetailedDailyCategories);
  const subCategories = useAppSelector(selectDetailedDailySubCategories);
  const subCategoriesValue = useMemo(() => {
    return (formData.DowntimeCodeCategory ? subCategories : [])?.map((c) => ({
      key: c.Key,
      value: c.Value,
    }));
  }, [formData.DowntimeCodeCategory, subCategories]);
  const dispatch = useAppDispatch();

  const handleInputChange = (name: string, value: string | Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error || undefined,
    }));
  };
  const prepareData = (): SaveEditDowntimeReq => {
    return {
      ...(editData?.Id ? { Id: editData.Id } : {}),
      WellId: editData?.WellId || wellId,
      Date: formData.Date?.toISOString() || '',
      EndDate: formData.EndDate?.toISOString() || '',
      DowntimeCodeCategory: formData.DowntimeCodeCategory || '',
      DowntimeCodeGrouping: formData.DowntimeCodeGrouping || '',
      Comment: formData.Comment || null,
      SystemFaultWord: formData.SystemFaultWord || null,
    };
  };
  const validateField = (name: string, value: string | Date | null | number): string => {
    if (name === 'Date' && !value) return 'Please select a start date';
    if (name === 'EndDate' && !value) return 'Please select a end date';
    return '';
  };
  const validateForm = (): boolean => {
    const newErrors: DetailedDailyFormErrors = {};
    let valid = true;

    ['Date', 'EndDate'].forEach((field) => {
      const value = formData[field as keyof SaveEditDowntimeReq] || null;
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };
  const handleSave = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    try {
      setSaving(true);
      const saveData = prepareData();
      const success = await saveDetailedDailyDowntime(saveData);
      if (success) {
        showSuccessToast('Downtime Successfully saved');
      } else {
        showErrorToast('Downtime save failure', 'There was an error saving the downtime. Please try again.');
      }
      if (success) {
        onSaved?.();
        onClose();
      }
    } catch {
      showErrorToast('Downtime save failure', 'There was an error saving the downtime. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    setFormData((prev) => ({ ...prev, DowntimeCodeGrouping: '' }));
    if (formData.DowntimeCodeCategory)
      dispatch(fetchDetailedDailySubCategories({ category: formData.DowntimeCodeCategory }));
  }, [formData.DowntimeCodeCategory]);

  useEffect(() => {
    if (isOpen) {
      setFormData(editData ? { ...editData } : {});
    }
  }, [editData, isOpen]);

  return isOpen
    ? createPortal(
      <div className='modal-overlay add-detail-daily-downtime'>
        <div className='modal-container'>
          <div className='modal-main-container'>
            <ModalHeader title={editData ? 'Edit detailed summary' : 'Add detailed summary'} onClose={onClose} />
            <main className='form-main'>
              <section className='form-section'>
                <div className='form-row'>
                  <FormField
                    label='Start date'
                    name='Date'
                    value={formData.Date || null}
                    onChange={handleInputChange}
                    type='date'
                    required
                    error={errors.Date}
                  />
                  <FormField
                    label='End date'
                    name='EndDate'
                    value={formData.EndDate || null}
                    onChange={handleInputChange}
                    type='date'
                    required
                    error={errors.EndDate}
                  />
                </div>
                <div className='form-row'>
                  <FormField
                    label='Lost oil'
                    name='LostOil'
                    disabled
                    value={formData.LostOil || null}
                    onChange={handleInputChange}
                  />
                  <FormField
                    label='Lost gas'
                    name='LostGas'
                    disabled
                    value={formData.LostGas || null}
                    onChange={handleInputChange}
                  />
                </div>
                <div className='form-row'>
                  <FormField
                    label='Lost water'
                    name='LostWater'
                    disabled
                    value={formData.LostWater || null}
                    onChange={handleInputChange}
                  />
                  <FormField
                    label='VSD fault'
                    name='SystemFaultWord'
                    value={formData.SystemFaultWord || null}
                    onChange={handleInputChange}
                    error={errors.SystemFaultWord}
                  />
                </div>
                <div className='form-row'>
                  <FormField
                    label='Category'
                    name='DowntimeCodeCategory'
                    value={formData.DowntimeCodeCategory || null}
                    onChange={handleInputChange}
                    type='select'
                    options={categories.map((c) => ({ key: c.Key, value: c.Value }))}
                  />
                </div>
                <div className='form-row'>
                  <FormField
                    label='Sub category'
                    name='DowntimeCodeGrouping'
                    value={formData.DowntimeCodeGrouping ? formData.DowntimeCodeGrouping : 'Select'}
                    onChange={handleInputChange}
                    type='select'
                    options={subCategoriesValue}
                  />
                </div>

                <CommentSection
                  label='Comments'
                  value={formData.Comment || ''}
                  onChange={(value) => handleInputChange('Comment', value)}
                />
              </section>
            </main>
            <footer className='form-footer'>
              <ActionButtons
                buttonText={editData ? 'Save downtime' : 'Add downtime'}
                onSubmit={handleSave}
                onCancel={onClose}
                isSubmitting={saving}
              />
            </footer>
          </div>
        </div>
      </div>,
      document.querySelector('.outlet-container') || document.body,
    )
    : null;
};
export default AddEditDowntime;
